/**
 * Service-worker lifecycle and connectivity UI (PLAN.md §5.27).
 *
 * Two things the user needs to be told, and nothing else:
 *
 * 1. **A new version is ready.** Updates are `registerType: 'prompt'`, not automatic — silently
 *    swapping the bundle under someone mid-reply reloads away their work. They choose when.
 * 2. **You are offline.** The shell still loads from cache, but no API call will succeed, so the
 *    banner says so plainly with the time data was last known good. Never let cached chrome
 *    imply the ticket data on screen is current.
 */

import { useEffect, useState } from 'react';
import { ArrowPathIcon, SignalSlashIcon } from '@heroicons/react/24/outline';
import { useRegisterSW } from 'virtual:pwa-register/react';

export function PwaStatus() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  const [online, setOnline] = useState(navigator.onLine);
  const [lastOnline, setLastOnline] = useState<Date | null>(navigator.onLine ? new Date() : null);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      setLastOnline(new Date());
    };
    const goOffline = () => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (!online) {
    return (
      <div
        role="status"
        className="flex items-center justify-center gap-2 bg-amber-500 px-4 py-1.5 text-sm font-medium text-white dark:bg-amber-600"
      >
        <SignalSlashIcon className="h-4 w-4" aria-hidden="true" />
        <span>
          Offline — what you see may be stale
          {lastOnline && ` (last updated ${lastOnline.toLocaleTimeString()})`}
        </span>
      </div>
    );
  }

  if (needRefresh) {
    return (
      <div
        role="status"
        className="flex items-center justify-center gap-3 bg-primary-500 px-4 py-1.5 text-sm font-medium text-white"
      >
        <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
        <span>A new version is available.</span>
        <button
          type="button"
          onClick={() => void updateServiceWorker(true)}
          className="rounded-md bg-white/20 px-3 py-0.5 transition-colors hover:bg-white/30"
        >
          Reload
        </button>
        <button
          type="button"
          onClick={() => setNeedRefresh(false)}
          className="text-white/80 underline-offset-2 hover:underline"
        >
          Later
        </button>
      </div>
    );
  }

  return null;
}
